import { useCallback } from 'react';
import { useTestContext } from '../../context/testContext';
import { useCalibration } from '../../context/calibrationContext';

const useStimulusPosition = () => {
  const { testData } = useTestContext();
  const {
    calibrationData: { blindSpotVanishingXDistancePx },
  } = useCalibration();

  const processAdjustments = useCallback(
    (coordinate, distanceBetweenStimulusAndFixationInPx, fixationTargetCoordinates) => {
      const boundaries = {
        x: window.screen.width,
        y: window.screen.height,
      };

      const fixationSize = {
        x: testData?.visualFieldSettings?.fixationTargetDimensions?.width,
        y: testData?.visualFieldSettings?.fixationTargetDimensions?.height,
      };

      const landoltCDimensions = { x: 150, y: 100 };

      const getCoordinateLimit = (coordinate) => {
        return boundaries[coordinate] - screenLimitBuffer(coordinate) - landoltCDimensions[coordinate];
      };

      const screenLimitBuffer = (coordinate) => {
        return boundaries[coordinate] * testData?.visualFieldSettings?.screenBuffer;
      };

      const getElementsAdjustment = () => {
        return (landoltCDimensions[coordinate] / 2) * -1;
      };

      const getFixationTargetReferencePosition = () => {
        return fixationTargetCoordinates[coordinate] + fixationSize[coordinate] / 2;
      };

      let newAdjustment = 0;
      let stimulusPositionForCoordinate = 0;
      let adjustedPosition = 0;
      let adjustedBoxPosition = 0;

      stimulusPositionForCoordinate = getFixationTargetReferencePosition() + distanceBetweenStimulusAndFixationInPx + getElementsAdjustment();

      if (stimulusPositionForCoordinate >= screenLimitBuffer(coordinate) && stimulusPositionForCoordinate <= getCoordinateLimit(coordinate)) {
        return { [`stimulusPoint${coordinate.toUpperCase()}`]: stimulusPositionForCoordinate };
      }

      const handleUnderflow = () => {
        if (stimulusPositionForCoordinate < 0) {
          stimulusPositionForCoordinate = stimulusPositionForCoordinate * -1;
          newAdjustment = stimulusPositionForCoordinate + screenLimitBuffer(coordinate);
        } else {
          newAdjustment = screenLimitBuffer(coordinate) - stimulusPositionForCoordinate;
        }
        adjustedPosition = screenLimitBuffer(coordinate);
        adjustedBoxPosition = fixationTargetCoordinates[coordinate] + newAdjustment;
      };

      const handleOverflow = () => {
        newAdjustment = stimulusPositionForCoordinate - getCoordinateLimit(coordinate);
        adjustedPosition = getCoordinateLimit(coordinate);
        adjustedBoxPosition = fixationTargetCoordinates[coordinate] - newAdjustment;
      };

      if (stimulusPositionForCoordinate < screenLimitBuffer(coordinate)) {
        handleUnderflow();
      } else if (stimulusPositionForCoordinate > getCoordinateLimit(coordinate)) {
        handleOverflow();
      }

      return { [`stimulusPoint${coordinate.toUpperCase()}`]: adjustedPosition, [`fixationTarget${coordinate.toUpperCase()}`]: adjustedBoxPosition };
    },
    [testData?.visualFieldSettings],
  );

  const getPosition = useCallback(
    (dotPosition, coordinate, fixationTargetCoordinates) => {
      if (coordinate === 'y') {
        dotPosition *= -1;
      }

      const newDotPositionForCoordinate = processAdjustments(coordinate, dotPosition, fixationTargetCoordinates);
      return newDotPositionForCoordinate;
    },
    [processAdjustments],
  );

  const getPixelsForDegree = useCallback(
    (degree) => {
      return (degree * blindSpotVanishingXDistancePx) / 15;
    },
    [blindSpotVanishingXDistancePx],
  );

  const getCoordinates = useCallback(
    (currentDot, fixationTargetCoordinates) => {
      const { stimulusPointY, fixationTargetY } = getPosition(getPixelsForDegree(currentDot['degreeY']), 'y', fixationTargetCoordinates);
      const { stimulusPointX, fixationTargetX } = getPosition(getPixelsForDegree(currentDot['degreeX']), 'x', fixationTargetCoordinates);

      return { stimulusPointX, stimulusPointY, fixationTargetX, fixationTargetY };
    },
    [getPixelsForDegree, getPosition],
  );

  return getCoordinates;
};

export default useStimulusPosition;
